"use client"

import type { CSSProperties, SVGProps } from "react"
import { IconMicrophoneOutline18 as Microphone } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { SpeechInput } from "./use-speech-input"
import { Button } from "./button"
import { CHAT_INPUT_IDLE_ICON_COLOR } from "./chat-input-icons"
import { barScales, useMicLevels } from "./use-mic-levels"

const BAR_X = [1.125, 5.625, 10.125, 14.625]
const SWAP =
	"transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"

// Mic and bars share one box so the swap crossfades in place; bar heights arrive as CSS variables.
function MicLevelsGlyph({ className, style }: SVGProps<SVGSVGElement>) {
	return (
		<span aria-hidden className={cn("relative", className)} style={style}>
			<Microphone
				className={cn(
					"absolute inset-0 h-full w-full group-data-[listening]/voice:scale-75 group-data-[listening]/voice:opacity-0",
					CHAT_INPUT_IDLE_ICON_COLOR,
					SWAP,
				)}
			/>
			<svg
				viewBox="0 0 18 18"
				fill="currentColor"
				className={cn(
					"absolute inset-0 h-full w-full scale-75 opacity-0 group-data-[listening]/voice:scale-100 group-data-[listening]/voice:opacity-100",
					SWAP,
				)}
			>
				{BAR_X.map((x, i) => (
					<rect
						key={x}
						data-voice-bar=""
						x={x}
						y={1.125}
						width={2.25}
						height={15.75}
						rx={1.125}
						className="origin-center transition-transform ease-out [transform-box:fill-box] [transition-duration:90ms] motion-reduce:transition-none"
						style={{ transform: `scaleY(var(--voice-bar-${i}, 0.2))` }}
					/>
				))}
			</svg>
		</span>
	)
}

/**
 * Dictate into the composer this button sits in. Renders nothing where the
 * browser has no recogniser, so no one clicks a control that cannot work.
 * The composer owns `speech`, so its send can end the dictation.
 */
export function VoiceInputButton({
	speech,
	disabled,
	className,
	label = "Dictate",
	stopLabel = "Stop dictating",
}: {
	speech: SpeechInput
	disabled?: boolean
	className?: string
	label?: string
	stopLabel?: string
}) {
	const { listening, toggle, supported } = speech
	const levels = useMicLevels(listening)

	if (!supported) return null

	const barStyle = Object.fromEntries(
		barScales(levels).map((scale, i) => [`--voice-bar-${i}`, scale]),
	) as CSSProperties

	return (
		<Button
			type="button"
			onClick={toggle}
			variant="ghost"
			Icon={MicLevelsGlyph}
			iconStyle={barStyle}
			aria-label={listening ? stopLabel : label}
			aria-pressed={listening}
			data-listening={listening ? "" : undefined}
			iconClassName={cn("h-4 w-4 flex-none", listening && "text-primary")}
			className={cn(
				"group/voice h-[30px] w-[30px] rounded-lg p-0 text-secondary hover:text-primary dark:text-secondary dark:hover:text-primary",
				listening &&
					"bg-gray-100 text-primary hover:bg-gray-100 dark:bg-white/[0.07] dark:text-primary dark:hover:bg-white/10",
				className,
			)}
			disabled={disabled}
		/>
	)
}
